import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import AppConfig from '@/AppConfig';
import LoadingScreen from '@/components/shared/LoadingScreen';
import { StuJourneyProvider } from '@/context/StuJourneyContext';
import useStuAccess from '@/hooks/useStuAccess';
import { isStuStartPath, STU_CLOSED_REDIRECT_PATH } from '@/utils/stu/access';

/**
 * Every super top-up journey page renders inside this base, with the journey's own state. Each
 * step page draws its own step header (L29). The start pages (home, members) wait for `/me` and
 * open only to members with `RETAIL_SUPER_TOP_UP` on (L32).
 *
 * Nothing from the retail journey: no retail provider, no forms provider, no agent gating. The
 * super top-up relay namespace admits no agent identity, so there is no agent session to handle.
 */
const BaseSuperTopup = () => {
  const { pathname } = useLocation();
  const isOpen = useStuAccess();

  useEffect(() => {
    document.title = AppConfig.RETAIL_HEALTH_INSURANCE_APP_NAME;
    return () => {
      document.title = 'Claims';
    };
  }, []);

  // The iOS app's super top-up WebView keeps its own spinner over the page until the page calls
  // `onLoadComplete` (L33). iOS defines `NativeBridge` once the document has parsed, so a call
  // before `load` can find nothing; wait for `load` if it hasn't fired. Outside the apps'
  // WebViews there is no `NativeBridge` and this does nothing.
  useEffect(() => {
    const notifyLoaded = () => window?.NativeBridge?.onLoadComplete?.();
    if (document.readyState === 'complete') {
      notifyLoaded();
      return undefined;
    }
    window.addEventListener('load', notifyLoaded, { once: true });
    return () => window.removeEventListener('load', notifyLoaded);
  }, []);

  if (isStuStartPath(pathname)) {
    if (isOpen === undefined) return <LoadingScreen />;
    if (!isOpen) return <Navigate to={STU_CLOSED_REDIRECT_PATH} replace />;
  }

  return (
    <StuJourneyProvider>
      <Outlet />
    </StuJourneyProvider>
  );
};

export default BaseSuperTopup;
